import type { PartialId } from '@attain-apps/string-builder'

import type * as dms from '../../dms'

import type { Node } from './node'

export interface Tree {
  /** @see {@link Node} */
  nodes: readonly Node[]
  /**
   * Optional bindings that tell tree-editor which node attributes should drive
   * visible card fields when the user edits a node locally.
   */
  displayBindings?: Tree.DisplayBindings
  /**
   * When creating a new {@link Node}, this is used if a more appropriate
   * {@link dms.models.ForRecord.Id} cannot be determined
   */
  defaultModelId?: dms.models.ForRecord.Id
  /**
   * Conveys how edges between {@link Node} are determined
   *
   * | Value | Meaning |
   * |:--|:--|
   * | `undefined` | {@link Node} are linked by parent-child relationships within a single {@link dms.models.Dimension} |
   * | {@link PartialId} | {@link Node} are linked by a many-to-one relationship using a reference {@link dms.models.Attribute} with the given {@link PartialId} |
   * | { ... } | {@link Node} are linked by a many-to-many relationship using an {@link https://en.wikipedia.org/wiki/Associative_entity associative entity} with two reference {@link dms.models.Attribute} |
   */
  edgesFrom?:
    | PartialId
    | {
        /**
         * {@link https://en.wikipedia.org/wiki/Associative_entity Associative entity}
         * representing the edges
         */
        association: dms.models.ForRecord.Id
        /**
         * {@link PartialId} of the reference {@link dms.models.Attribute} that
         * represents the parent {@link Node}
         */
        parent: PartialId
        /**
         * {@link PartialId} of the reference {@link dms.models.Attribute} that
         * represents the child {@link Node}
         */
        child: PartialId
        /**
         * When creating a new edge, these are the default values used for the
         * other attributes besides the {@link parent} and {@link child}
         * reference attributes
         */
        defaults?: dms.data.Record.Values.Input
      }
}
export namespace Tree {
  /**
   * Visible card fields that tree-editor can update locally from node edits.
   */
  export type DisplayField = 'label' | 'title' | 'sublabel'
  export namespace DisplayField {
    export const ALL = [
      'label',
      'title',
      'sublabel',
    ] as const satisfies readonly DisplayField[]
  }

  /**
   * Describes how a visible card field should be derived from an edited node
   * attribute.
   */
  export interface DisplayBinding {
    /**
     * Partial attribute ID inside {@link Node.values} that drives the field.
     */
    readonly attr: PartialId
    /**
     * How to render the edited value locally before the chart is re-queried.
     */
    readonly source: DisplaySource
  }

  /**
   * Optional per-field display binding contract for tree-editor widgets.
   */
  export type DisplayBindings = Partial<Record<DisplayField, DisplayBinding>>

  /**
   * Supported local rendering strategies for edited node attributes.
   */
  export type DisplaySource = 'value-string' | 'reference-breadcrumb'
  export namespace DisplaySource {
    export const ALL = [
      'value-string',
      'reference-breadcrumb',
    ] as const satisfies readonly DisplaySource[]
  }
}
